import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import Confirmation, { ConfirmationProps } from 'shared/components/confirmation-modal'
import { BASE_PATH_URL } from 'shared/constants/base-path-url'
import { useLocoTranslation } from '../hooks/use-loco-translation'
import { SKIP_CONFIRMATION_EXIT_ONCE_EVENT } from './event'

export interface ConfirmationModalBeforeExit
  extends Omit<ConfirmationProps, 'opened' | 'onClose' | 'onConfirm'> {
  hideModal?: boolean
  shouldSkipOnRouteChange?: (route: string) => boolean
}

export const ConfirmationModalBeforeExit = (properties: ConfirmationModalBeforeExit) => {
  const { t } = useLocoTranslation()

  const {
    confirmationText = t('confirmation_modal.confirm_text_before_exit'),
    hideModal,
    shouldSkipOnRouteChange,
    ...props
  } = properties

  const router = useRouter()

  const skipOnce = useRef<boolean>(false)

  const [hasConfirmed, setHasConfirmed] = useState(false)
  const [nextRoute, setNextRoute] = useState<string | null>(null)
  const [isModalOpened, setIsModalOpened] = useState<boolean>(false)

  useEffect(() => {
    const handleDontShowModal = () => {
      skipOnce.current = true
    }

    window.addEventListener(SKIP_CONFIRMATION_EXIT_ONCE_EVENT, handleDontShowModal)
    return () => window.removeEventListener(SKIP_CONFIRMATION_EXIT_ONCE_EVENT, handleDontShowModal)
  }, [t, hasConfirmed, confirmationText, hideModal])

  useEffect(() => {
    if (!hideModal) {
      const handleWindowClose = (e: BeforeUnloadEvent) => {
        if (!skipOnce.current) {
          if (!hasConfirmed) {
            e.preventDefault()
            e.returnValue = confirmationText
            return e.returnValue
          }
        } else {
          skipOnce.current = false
        }
      }

      window.addEventListener('beforeunload', handleWindowClose)
      return () => window.removeEventListener('beforeunload', handleWindowClose)
    }
  }, [t, hasConfirmed, confirmationText, hideModal])

  useEffect(() => {
    if (!hideModal) {
      if (!skipOnce.current && hasConfirmed && nextRoute) {
        router.push(nextRoute)
      }

      const onRouteChangeStart = (route: string) => {
        if (shouldSkipOnRouteChange && shouldSkipOnRouteChange(route)) return

        if (!skipOnce.current) {
          if (!hasConfirmed) {
            const normalizedRoute = route === BASE_PATH_URL ? '/' : route.replace(BASE_PATH_URL, '')
            setNextRoute(normalizedRoute)
            setIsModalOpened(true)
            router.events.emit('routeChangeError')

            // TODO: can we ignore this in the Rollbar?
            throw 'navigation aborted by user'
          }
        } else {
          skipOnce.current = false
        }
      }

      router.events.on('routeChangeStart', onRouteChangeStart)
      return () => router.events.off('routeChangeStart', onRouteChangeStart)
    }
  }, [nextRoute, hasConfirmed, router, hideModal])

  return (
    <Confirmation
      opened={isModalOpened}
      onClose={() => {
        setHasConfirmed(false)
        setIsModalOpened(false)
        setNextRoute(null)
      }}
      onConfirm={async () => {
        setIsModalOpened(false)
        setHasConfirmed(true)
        /* NOTE: if delete it, we'll get error:
          "next-dev.js:20 Cannot remove a child from a different parent <div data-headlessui-portal>​</div>​ <div id=​"headlessui-portal-root">​</div>​
          window.console.error @ next-dev.js:20"
            
        } */
        await new Promise(resolve => resolve)
      }}
      confirmationText={confirmationText}
      {...props}
    />
  )
}
